/**
 * Unified streaming library types.
 *
 * All streaming in the app (SSE and NDJSON) flows through these interfaces.
 * Consumers configure a stream via {@link IStreamConfig} and receive events
 * via {@link IStreamCallbacks}. The library handles connection, parsing,
 * retry, auth, error classification, and observability internally.
 */

/**
 * Supported streaming protocols.
 */
export enum EStreamProtocol {
  /** Server-Sent Events — long-lived connection with server-push events. */
  SSE = 'SSE',
  /** Newline-Delimited JSON — request/response with a streamed body. */
  NDJSON = 'NDJSON',
}

/**
 * Connection lifecycle states.
 */
export enum EStreamState {
  /** No active connection. */
  IDLE = 'IDLE',
  /** Connection attempt in progress. */
  CONNECTING = 'CONNECTING',
  /** Connected and receiving data. */
  CONNECTED = 'CONNECTED',
  /** Disconnected, attempting to reconnect. */
  RECONNECTING = 'RECONNECTING',
  /** Connection closed normally (consumer or server initiated). */
  CLOSED = 'CLOSED',
  /** Connection failed and will not retry. */
  FAILED = 'FAILED',
}

/**
 * Retry backoff strategies.
 */
export enum ERetryStrategy {
  /** delay = baseDelayMs * 2^(attempt-1), capped at maxDelayMs. */
  EXPONENTIAL = 'EXPONENTIAL',
  /** delay = baseDelayMs * attempt, capped at maxDelayMs. */
  LINEAR = 'LINEAR',
  /** delay = baseDelayMs (constant). */
  FIXED = 'FIXED',
}

/**
 * Retry configuration shared by all stream types.
 */
export interface IRetryConfig {
  /** Maximum number of retry/reconnect attempts. */
  maxAttempts: number;
  /** Backoff strategy between attempts. */
  strategy: ERetryStrategy;
  /** Base delay in milliseconds. */
  baseDelayMs: number;
  /** Upper bound on computed delay. Defaults to 30 000 ms. */
  maxDelayMs?: number;
}

/**
 * Information passed to the onRetry callback.
 */
export interface IRetryInfo {
  /** 1-based attempt number. */
  attempt: number;
  /** Configured maximum attempts. */
  maxAttempts: number;
  /** Delay before this attempt in milliseconds. */
  delayMs: number;
  /** Why the retry was triggered. */
  reason: string;
}

/**
 * Metadata attached to each parsed message.
 */
export interface IMessageMeta {
  /** SSE event ID (for Last-Event-ID resumption). Undefined for NDJSON. */
  eventId?: string;
  /** SSE event type field. Undefined for NDJSON. */
  eventType?: string;
}

/**
 * Error classification categories for stream interruptions.
 */
export enum EStreamErrorType {
  /** Intentional cancellation — never retry, never report. */
  USER_INITIATED = 'USER_INITIATED',
  /** Transient failure that may succeed on retry. */
  RECOVERABLE = 'RECOVERABLE',
  /** Non-transient failure — surface immediately, do not retry. */
  NON_RECOVERABLE = 'NON_RECOVERABLE',
}

/**
 * Classified stream error with diagnostic context.
 */
export interface IStreamError {
  /** Error classification. */
  type: EStreamErrorType;
  /** The original error. */
  error: Error;
  /** Human-readable reason for diagnostics. */
  reason: string;
  /** HTTP status code if the error originated from a response. */
  httpStatus?: number;
}

/**
 * Observability context for Sentry integration.
 */
export interface IStreamObservability {
  /** Module or feature name (e.g. 'runs', 'quill-chat', 'notifications'). */
  module: string;
  /** Operation name (e.g. 'stream_run_events', 'send_message'). */
  operation: string;
  /** Extra context merged into Sentry captures. */
  extra?: Record<string, unknown>;
}

/**
 * Auth configuration for stream requests.
 */
export interface IStreamAuth {
  /** Returns a bearer token. Called before each connection attempt. */
  getToken: () => Promise<string>;
  /** When true, sends X-E2E-Test-Mode header instead of Authorization. */
  e2eTestMode?: boolean;
}

/**
 * Callbacks for stream events. All callbacks are optional except onMessage.
 */
export interface IStreamCallbacks<T> {
  /** Called for each parsed message from the stream. */
  onMessage: (data: T, meta: IMessageMeta) => void;
  /** Called when the connection state changes. */
  onStateChange?: (state: EStreamState, prevState: EStreamState) => void;
  /** Called when a non-recoverable or exhausted error occurs. */
  onError?: (streamError: IStreamError) => void;
  /** Called when the stream completes normally (NDJSON end-of-body, or SSE server close). */
  onComplete?: (results: T[]) => void;
  /** Called before each retry attempt. */
  onRetry?: (info: IRetryInfo) => void;
}

/**
 * Core configuration for creating a stream connection.
 *
 * @typeParam T - The type of parsed messages.
 */
export interface IStreamConfig<T> {
  /** Stream endpoint URL. */
  url: string;
  /** Protocol to use for parsing the response. */
  protocol: EStreamProtocol;
  /** HTTP method. Defaults to GET for SSE, POST for NDJSON. */
  method?: string;
  /** Additional headers merged with auth headers. */
  headers?: Record<string, string>;
  /** Request body (typically used with NDJSON POST requests). */
  body?: unknown;
  /** Authentication configuration. */
  auth?: IStreamAuth;
  /** Retry/reconnect configuration. */
  retry?: Partial<IRetryConfig>;
  /** Request timeout in milliseconds. Applies per attempt. Default: 30 000ms for NDJSON, none for SSE. */
  timeoutMs?: number;
  /** HTTP status codes that should never be retried. Default: [401, 403, 404, 410]. */
  fatalHttpStatuses?: number[];
  /** Extra retryable status codes, on top of the built-in 408, 429, 502, 503, 504. `fatalHttpStatuses` wins. */
  retryableHttpStatuses?: number[];
  /** Observability context for Sentry reporting. */
  observability?: IStreamObservability;
  /** Custom parser for raw event data. Defaults to JSON.parse. */
  parse?: (raw: string) => T;
  /** External AbortSignal (e.g. from component lifecycle). */
  signal?: AbortSignal;

  // SSE-specific options

  /** Initial Last-Event-ID for SSE resumption. */
  lastEventId?: string;
  /**
   * Keep SSE connection open when the browser tab is hidden. Default: true.
   *
   * **Not yet implemented** — accepted for forward-compatibility but currently
   * has no effect. The connection stays open regardless of visibility state.
   */
  openWhenHidden?: boolean;
  /** For SSE: whether to reconnect when the server closes the connection. Default: true. */
  reconnectOnClose?: boolean;
  /** Minimum delay (ms) before reconnecting after a normal SSE close. Prevents tight loops. Default: 1000. */
  reconnectCooldownMs?: number;
}

/**
 * Handle to an active stream connection.
 */
export interface IStreamConnection {
  /** Current connection state. */
  readonly state: EStreamState;
  /** Last SSE event ID received (for resumption). */
  readonly lastEventId: string | undefined;
  /** Close the connection. Safe to call multiple times. */
  close: () => void;
}

/**
 * Parsed SSE event from raw bytes.
 */
export interface IParsedSSEEvent {
  /** Event data (concatenated `data:` lines). */
  data: string;
  /** Event ID (`id:` field). */
  id?: string;
  /** Event type (`event:` field). */
  event?: string;
}
